import { useMemo } from 'react';
import { useListCoursesBySchoolYear } from '@dataconnect/generated/react';

// ----------------------------------------------------------------------

/**
 * A course the teacher can author against — the app-wide "active course" scope,
 * normalized so it carries the same nested `schoolYear` shape whether it was
 * resolved from a class the teacher teaches (`GetMyClasses`) or, for a class-less
 * Department Head, from their school-year membership (`GetMySchoolYears` +
 * `ListCoursesBySchoolYear`). Curriculum / Lesson Plans / Standards all consume it.
 */
export type ActiveCourse = {
  id: string;
  name: string;
  subject: string | null;
  gradeLevel: string | null;
  schoolYear: { id: string; name: string; school: { id: string; name: string } };
};

// ----------------------------------------------------------------------

/**
 * The courses a staff member may manage in a given school year — **the forward-compat
 * seam.** Today this is simply every course offered in the year (`ListCoursesBySchoolYear`,
 * `@auth(level: USER)`); a future per-course / per-department ownership model (a
 * `CourseStaff` table, or a `course.subject`/`department` match) changes ONLY this hook —
 * the course switcher, the authoring views, and `usePermissions()` are all downstream of it
 * and don't move. See docs/architecture/permissions.md and epic #143.
 *
 * `ListCoursesBySchoolYear` returns flat course rows with no `schoolYear` nesting, so the
 * caller supplies `schoolYearMeta` (already known from the active class or membership) which
 * we graft on to produce the normalized `ActiveCourse` shape.
 */
export function useManagedCourses(
  schoolYearId: string | null,
  schoolYearMeta: ActiveCourse['schoolYear'] | null
): { courses: ActiveCourse[]; isPending: boolean } {
  const q = useListCoursesBySchoolYear(
    { schoolYearId: schoolYearId ?? '' },
    { enabled: !!schoolYearId }
  );

  const courses = useMemo<ActiveCourse[]>(() => {
    if (!schoolYearMeta) return [];
    return (q.data?.courses ?? []).map((c) => ({
      id: c.id,
      name: c.name,
      subject: c.subject ?? null,
      gradeLevel: c.gradeLevel ?? null,
      schoolYear: schoolYearMeta,
    }));
  }, [q.data, schoolYearMeta]);

  return { courses, isPending: !!schoolYearId && q.isPending };
}
